package com.dynamicruntime.webapp

import com.dynamicruntime.common.home.HFRAG
import com.dynamicruntime.common.app.EnvAuthOp
import com.dynamicruntime.common.endpoint.EP
import com.dynamicruntime.common.home.HACT
import com.dynamicruntime.common.uiblock.UiCall
import com.dynamicruntime.common.uiblock.UiRoute
import com.dynamicruntime.common.user.PERSONA
import com.dynamicruntime.common.user.UserChoice
import com.dynamicruntime.common.util.isVariableName
import com.dynamicruntime.common.util.splitComma
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.browser.document
import org.w3c.dom.Element
import react.ChildrenBuilder
import react.FC
import react.Props
import react.create
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.header
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useRef
import react.useState
import web.cssom.ClassName

private val appBarScope = MainScope()

/** What the app bar says about identity when nobody is signed in (issue #276). */
const val signedOutLabel = "Signed out"

/**
 * What the app bar should say about who is signed in -- or **null to say nothing yet** (issue #276).
 *
 * Three states, and the third is the one that makes this a function rather than an `if`. The shell renders
 * before `/home/ui/config` returns, so until it does the answer is *unknown*, not "signed out": claiming
 * signed-out on the first paint would be wrong for a moment and would flicker for a signed-in user on every
 * load. [loaded] is what separates "we asked and nobody is" from "we have not asked".
 *
 * A signed-in caller is announced by their [displayName] -- the business name for an entity account, the
 * personal name otherwise, a distinction the caller resolves via `UserProfile.displayName`. A name is itself
 * the statement that somebody is signed in. When they have none, the label falls back to [signedInFallback]
 * rather than to silence -- silence is the state this whole function exists to stop being ambiguous.
 *
 * Pure, and covered under `jsNodeTest`.
 */
fun identityLabel(loaded: Boolean, isLoggedIn: Boolean, displayName: String?): String? = when {
    !loaded -> null
    !isLoggedIn -> signedOutLabel
    else -> displayName?.trim()?.ifEmpty { null } ?: signedInFallback
}

/**
 * Whether the session ended between two reads of the shell config: signed in on the [previous] read, signed out on
 * this one ([nowLoggedIn]). A session that timed out, a restarted in-memory instance, or a sign-out in another tab all
 * look like this, and none of them is a page's to recover from: the page on screen was drawn for a caller who is gone,
 * and re-reading it as nobody does not work well. So the shell goes home, as **Log out** does.
 *
 * Only that transition counts. The first read ([previous] null) is not a change, and a read that failed never gets
 * here (the server being down, mid-restart, is not a sign-out); signing *in* is handled by whatever signed in. Pure,
 * and covered under `jsNodeTest`.
 */
fun sessionEnded(previous: Boolean?, nowLoggedIn: Boolean): Boolean = previous == true && !nowLoggedIn

/** Who one read of the shell config was for (issue #1547): whether signed in, and as which user. */
data class SessionSeen(val loggedIn: Boolean, val userId: Long)

/**
 * Whether the page on screen was drawn for a caller who is no longer the one signed in, so the shell goes home: the
 * session ended ([sessionEnded]), or it now acts as another signed-in user and this tab did not make that change
 * ([changedHere] false) -- an invitation accepted, or a switch made, in another tab of the browser (issue #1547). A change
 * this tab made lands where its caller sent it ([afterSessionChange]): Profile's switch stays on Profile. The first read
 * ([previous] null) and a sign-in from nobody are no change. Pure, and covered under `jsNodeTest`.
 */
fun callerLeft(previous: SessionSeen?, now: SessionSeen, changedHere: Boolean): Boolean =
    sessionEnded(previous?.loggedIn, now.loggedIn) ||
        (previous?.loggedIn == true && now.loggedIn && now.userId != previous.userId && !changedHere)

/** Shown in place of a name for a signed-in caller who has none -- still says *signed in*, which is the point. */
const val signedInFallback = "Signed in"

/**
 * The identity badge's text (issue #749): the [label] -- who is signed in -- followed, for a person with more
 * than one user, by **which** of their users this is, in brackets: `Demo Person [hub · Member B]`,
 * `Ada [Admin]`. The bracket holds only what tells this user from the person's others
 * (`UserChoice.qualifierWithin`); a person with one user sees the label alone, as before. Given the session's
 * [roles], a persona shown there says the access the user has when that differs from its persona's (issue #1450):
 * `Ada [Member (Administrator access)]`. Pure, covered under `jsNodeTest`.
 */
fun identityBadgeText(label: String, users: List<UserChoice>, roles: Collection<String>? = null): String {
    val current = users.firstOrNull { it.isCurrent } ?: return label
    val qualifier = current.qualifierWithin(users, roles)
    return if (qualifier.isEmpty()) label else "$label [$qualifier]"
}

/**
 * The identity badge's tooltip, for the [current] user: which of the person's users this is, by its full label, and
 * -- given the session's [roles] -- the access it has when that differs from its persona's (issue #1450), so the
 * tooltip does not say "Member" of someone with administrator access. Pure, covered under `jsNodeTest`.
 */
fun identityBadgeTitle(current: UserChoice, roles: Collection<String>?): String {
    val access = roles?.let { PERSONA.actsAs(current.persona, it) }
    return "You are signed in as ${current.label()}" + (access?.let { ", with ${PERSONA.accessPhrase(it)}." } ?: ".")
}

/**
 * The users the identity badge offers to switch to (issue #749): the list as the backend sent it when there is
 * someone in it to become -- more than one, or one that is not the user the session acts as -- and nothing
 * otherwise: a menu with a single entry, the user you already are, is a control that does nothing, so the badge
 * stays a plain label. A session acting as a user the list leaves out -- a sandbox user nobody stands behind any
 * more (issue #1197) -- is offered its one entry, since that is its way out. Pure, covered under `jsNodeTest`.
 */
fun switchableUsers(users: List<UserChoice>): List<UserChoice> =
    if (users.size > 1 || users.singleOrNull()?.isCurrent == false) users else emptyList()

/**
 * One row of the identity badge's menu: what tells [user] from the others in [users], in the badge's brackets --
 * or, when nothing does because it is the only entry (a stranded sandbox session's way out, issue #1197), its full
 * label, since an empty pair of brackets names nobody. The session's [roles] speak for the current user's row only
 * (issue #1450): the list carries nobody's roles, and the session knows only its own. Pure, covered under `jsNodeTest`.
 */
fun userMenuEntryText(user: UserChoice, users: List<UserChoice>, roles: Collection<String>? = null): String =
    user.qualifierWithin(users, roles.takeIf { user.isCurrent }).let { if (it.isEmpty()) user.label() else "[$it]" }

/**
 * What the app bar's brand area should show -- an explicit **three-state** rather than inferring from an absent
 * value (issue #469). Same reasoning as [identityLabel] one element to the left: a blank wordmark is *ambiguous*
 * between "still loading" and "the fetch failed", and #456 reintroduced exactly that ambiguity by moving the
 * wordmark into a `home.brand` fragment fetched over two round trips.
 */
sealed class ShellBrand {
    /** Neither the config nor its copy has arrived; the mark carries the bar (with a delayed progress cue). */
    object Loading : ShellBrand()
    /** Loaded. [label] is the wordmark, or null when the deployment names no brand -- then the mark stands alone. */
    class Ready(val label: String?) : ShellBrand()
    /** The copy could not be loaded (and a stale ref did not recover); mark alone, the body reports the outage. */
    object Failed : ShellBrand()
}

/**
 * The brand state to move to after a load attempt (issue #469), or **null to keep the current one**.
 *
 * A success shows the brand -- or the mark alone when it is blank, a deployment that names none. A failure
 * downgrades to [ShellBrand.Failed] only when nothing has loaded yet (`everLoaded` false); once a wordmark has shown, a
 * transient re-fetch failure keeps it -- "the previous wordmark beats no wordmark", which is what lets a refresh
 * never flicker. The initial [ShellBrand.Loading] is the `useState` default, never re-entered here. Pure, covered under
 * `jsNodeTest`.
 */
fun brandAfterLoad(loaded: Boolean, brand: String?, everLoaded: Boolean): ShellBrand? = when {
    loaded -> ShellBrand.Ready(brand?.trim()?.ifEmpty { null })
    everLoaded -> null
    else -> ShellBrand.Failed
}

/**
 * Whether a failed fragment fetch is a **stale build id** rather than a genuine outage (issue #469): a rolling
 * deploy leaves every open browser holding a ref no node recognizes, which 404s -- the ordinary case, not an
 * edge one. Distinguishable by the status alone, which is what lets the recovery ([fetchCopyWithRetry]) be
 * automatic. Pure, covered under `jsNodeTest`.
 */
fun isStaleFragment(status: Int?): Boolean = status == 404

/**
 * The two env-auth facts the bar draws (issue #360): whether the control should exist at all, and what it
 * currently says.
 *
 * Passed in rather than read from [appConfig] here, because that cache is filled **asynchronously** and
 * nothing re-renders when it lands -- a component reading it in an effect keyed on the refresh generation runs
 * before the fetch resolves and then never looks again. `App` already owns config-derived state for exactly
 * this reason (`debugAllowed`, `idleBumpIntervalMs`); these join it rather than inventing a second way.
 */
external interface AppBarProps : Props {
    /** The page the router is showing (issue #554), so the menu can mark it and open its group. */
    var currentPage: String
    /**
     * Called when the item for [currentPage] is chosen again (issue #565). Navigating to the page you are on
     * changes no address, so nothing else would react; `App` answers by remounting the page, which is the reset a
     * person means by it -- a login stranded at the code step is the case that found this.
     */
    var onRevisit: () -> Unit
    /**
     * Told what the shell config says of the caller, each time it is read (issue #1091): `App` provides it to the
     * pages, which name themselves by it ([ShellFacts]). Not called when the config could not be read, so a failed
     * refresh leaves the pages named as they were.
     */
    var onShellFacts: (ShellFacts) -> Unit
    /** Whether env auth exists on this channel -- decides whether the control is shown at all. */
    var envAuthSuppressible: Boolean
    /** Whether the session is currently *acting* env-authed -- decides what the control says. */
    var envAuthActing: Boolean
    /**
     * Whether the session is in the debug state (issue #517). Not read by the badge -- that is a plain on/off
     * toggle -- but it gates the persistent `_debug` box, which is shown only while debug is on.
     */
    var envAuthDebug: Boolean
    /** The `_debug` words the box offers to append (issue #1334), from the app config. */
    var debugTags: List<DebugTagChoice>
}

/**
 * The persistent top app bar: a brand on the left, and a hamburger menu on the right.
 *
 * **The menu is data.** Its items come whole from the home/shell UI-config (`state.menu`), which the backend
 * composes for the *current caller* -- so what a user may reach is decided once, on the side that knows. The
 * bar renders the list it is handed, in order, and adds nothing of its own: an item this user may not have is
 * simply absent from the response. That is what lets an entry like user administration appear for an
 * administrator and for nobody else without the frontend knowing anything about roles.
 *
 * Each item carries one [MenuItem.action]: a route to navigate to, or a call to run (issue #483) -- today
 * only logging out, which cannot be a link because it is a request plus a redirect.
 *
 * It re-reads the config on every refresh generation, so signing in or out (or being granted a capability)
 * redraws the menu.
 */
val AppBar = FC<AppBarProps> { props ->
    // The one conditional fault that cannot live in a dedicated component (issue #227): proving the *backstop*
    // boundary catches means breaking the chrome, and the chrome is what renders outside the page boundary.
    // Gated on the deployment's allowDebugPages, so on a real deployment this line can never fire.
    if (shouldFailShell()) {
        error("Deliberate shell fault from the debug page (issue #227).")
    }
    var open by useState(false)
    // Whether the identity badge's user menu is open (issue #749); independent of the hamburger's.
    var userMenuOpen by useState(false)
    var sandboxInfoOpen by useState(false)
    // The shell's copy, once read: what the sandbox chip's "What is this?" explains (issue #1337).
    var shellCopy by useState(Copy.empty)
    var sharedDemoInfoOpen by useState(false)
    // Which drill-down groups are expanded (issue #540). Collapsed by default -- a group's children show on
    // demand, keeping a long menu short -- and remembered per browser, so an operator who opens the Operator
    // group finds it open next time. Held as the set of expanded parent ids.
    var expandedGroups by useState { readExpandedGroups() }
    var config by useState<HomeConfig?>(null)

    // The persistent `_debug` box's value (issue #517, slice 3), seeded from this browser's storage. A hook, so
    // it is declared unconditionally here even though the box renders only in debug -- and hiding the box (on
    // leaving debug) never clears it, so the value is still here when debug returns.
    var debugTags by useState { debugRequestTags() }
    // The brand's three-state, held directly (issue #469): the effect knows which of loading/ready/failed
    // happened, so it writes the state rather than the bar inferring it from an empty [copy] -- which is
    // ambiguous, since a deployment that names no brand is *also* empty.
    var brandState by useState<ShellBrand>(ShellBrand.Loading)
    // Refs, not state: [everLoaded] is monotonic (a wordmark, once shown, is kept through a transient refresh
    // failure), and [genRef] -- a token per read -- lets a slow, superseded read's late result bow out instead of
    // clobbering a newer one. Neither is read for rendering, so neither needs to trigger one.
    val everLoaded = useRef(false)
    val genRef = useRef(0)
    // Who the last loaded shell config was for; null until one has loaded. A ref, not state: it only feeds [callerLeft],
    // never a render.
    val seenRef = useRef<SessionSeen>(null)
    val generation = useRefreshGeneration()
    val polls = useIdlePolls()
    val bump = useRefreshBump()

    // Re-read the shell config on every refresh generation -- mount, navigation, and any state mutation
    // (notably sign-in / sign-out) -- and on every idle poll, the shell config being a polled endpoint (issue #1372).
    // The menu stays as it was if the config could not be loaded.
    useEffect(generation, polls) {
        val token = (genRef.current ?: 0) + 1
        genRef.current = token
        appBarScope.launch {
            val cfg = apiResult { HomeApi.fetchConfig() }.valueOrNull()
            if (genRef.current != token) return@launch // a newer read is already in charge
            if (cfg == null) {
                // Config failed: Failed on a first load, but keep any wordmark already shown on a refresh.
                brandAfterLoad(loaded = false, brand = null, everLoaded = everLoaded.current == true)
                    ?.let { brandState = it }
                return@launch
            }
            config = cfg
            // The caller left since the last read -- the session ended (a timeout, a restarted instance, a sign-out in
            // another tab), or another tab signed in as someone else (an invitation accepted there, issue #1547): go
            // home, as Log out does, rather than leave a page drawn for a caller who is gone. Noticed on the idle
            // poll too, which is the point -- a tab left open moves home without being touched (a hidden tab polls
            // nothing, so it moves the moment it is looked at again). A page holding unsaved work is still asked
            // first, through the leave guard. Nothing here renews a session: it acts only once the caller is gone.
            val seen = SessionSeen(cfg.user.isLoggedIn, cfg.user.userId)
            val previous = seenRef.current
            seenRef.current = seen
            if (callerLeft(previous, seen, changedHere = takeSessionChangedHere())) {
                navigate(emptyList())
                // Navigating to the address on screen (already home) tells nobody, so bump as logoutAction does.
                bump()
            }
            // Moments are shown in the client's zone (issue #1115), set before the facts that redraw the page.
            DisplayZone.set(cfg.timeZone, cfg.timeZoneLabel)
            props.onShellFacts(shellFactsOf(cfg))
            // The tab icon follows the client (issue #1130) -- from a loaded config only, as the tint below, so a
            // failed refresh leaves the icon as it was.
            applyClientFavicon(clientFaviconUrl(cfg))
            // Tint the page while in a sandbox (issue #1049) -- set from a loaded config only, so a failed refresh
            // leaves the page as it was rather than dropping the signal.
            markPageSandbox(pageInSandbox(cfg))
            // The wordmark is a client's to change (issue #456), so it is re-read when the caller changes, not
            // only on mount. A stale build id (a rolling deploy) recovers silently via the shared retry.
            val loaded = apiResult {
                fetchCopyWithRetry(cfg.fragment) { apiResult { HomeApi.fetchConfig().fragment }.valueOrNull() }
            }
            if (genRef.current != token) return@launch
            val copy = loaded.valueOrNull()
            if (copy != null) {
                everLoaded.current = true
                shellCopy = copy
            } else {
                // Never swallow (webapp/CLAUDE.md): the chrome does not *show* an outage, but a Failed that
                // renders nothing must at least reach the console, or a browser test cannot assert its absence.
                loaded.failureOrNull()?.let {
                    console.error("$errorLogPrefix could not load app-bar copy: ${it.message}")
                }
            }
            // The shell's copy carries words every page uses (a "What is this?" link's, issue #1330), so the facts are
            // told again with it; unchanged facts are kept as they were, so this redraws nothing when nothing moved.
            copy?.let { props.onShellFacts(shellFactsOf(cfg, it)) }
            brandAfterLoad(copy != null, copy?.opt(HFRAG.homeNs, HFRAG.brand), everLoaded.current == true)
                ?.let { brandState = it }
        }
    }

    fun logoutAction() {
        open = false
        appBarScope.launch {
            apiResult { AuthApi.logout() }
            navigate(emptyList())
            // Bump so the menu (and every config consumer) re-reads even when we were already home -- navigating
            // to the address on screen tells nobody, which is why the direct re-read used to be needed here.
            bump()
        }
    }

    // Switching user (issue #749): a fresh session as another of the person's users, then home and a bump, the
    // way a login completes -- no page reload (issue #1015): every per-caller surface re-reads on the bump or fetches
    // as it mounts, and going home unmounts the page that was showing. A failure stays in the console (never
    // swallowed), and the menus close either way. From inside a sandbox it goes through the leave (issue #1197),
    // which a sandbox user acting with no roles may still make.
    fun switchUserAction(userId: Long) {
        userMenuOpen = false
        sandboxInfoOpen = false
        val leaving = switchesFromSandbox(config)
        appBarScope.launch {
            val switched = apiResult { if (leaving) AuthApi.leaveSandbox(userId) else AuthApi.switchUser(userId) }
            switched.failureOrNull()?.let {
                console.error("$errorLogPrefix could not switch user: ${it.message}")
                return@launch
            }
            afterSessionChange(bump)
        }
    }

    // Opening the sandbox (issue #931): S's open endpoint moves the session to the person's own user in their
    // client's sandbox -- a fresh session, like a switch -- so it lands the same way (issue #1015). A refusal stays in
    // the console, as a switch's does.
    fun openSandboxAction() {
        appBarScope.launch {
            val opened = apiResult { AuthApi.openSandbox() }
            opened.failureOrNull()?.let {
                console.error("$errorLogPrefix could not open the sandbox: ${it.message}")
                return@launch
            }
            afterSessionChange(bump)
        }
    }

    // Env logout (issue #486): clear the perimeter cookie, then leave the app for where the edge says to land.
    // Both URLs come from the call's arguments -- the edge supplies them because only it knows them -- and both
    // arrive already guarded same-origin by `FrontendActions` (issue #498). Unlike `logout`, this is a
    // full-window navigation, not an address change: the caller is now anonymous, and the destination is served by
    // the edge rather than being a route within this app.
    fun envLogoutAction(args: List<String>) {
        open = false
        val logoutPath = args.getOrNull(0) ?: return
        val landingUrl = args.getOrNull(1) ?: return
        appBarScope.launch {
            apiResult { Http.getApi(logoutPath) }
            leaveAppTo(landingUrl)
        }
    }

    // Built after the functions they call: a local `val` cannot forward-reference a local `fun`.
    val frontendActions = FrontendActions(
        logout = { logoutAction() },
        envLogout = { args -> envLogoutAction(args) },
        // openPath leaves the SPA for a same-origin server path (issue #493). The path is already guarded
        // same-origin by FrontendActions; here it is just the navigation. Closing the menu first, as the
        // other actions do.
        openPath = { path ->
            open = false
            leaveAppTo(path)
        },
        // Turn debug on/off (issue #517): move the session to debug or plain-on through the env-auth endpoint,
        // then re-read the config and bump so the menu (and the badge) redraw for the new state.
        setEnvDebug = { on ->
            open = false
            appBarScope.launch {
                setEnvAuthOp(if (on) EnvAuthOp.debug else EnvAuthOp.restore)
                bump()
            }
        },
        // Become the deployment admin (issue #1183) and go home re-reading, as the Home card's button does.
        becomeDeploymentAdmin = {
            open = false
            becomeDeploymentAdmin(bump)
        },
    )

    // The bar takes on the elevated-privilege look while the caller holds administrative rights. It reads the
    // same `canManageUsers` capability the menu does, so the cue and the Users item can never disagree -- and
    // when that capability narrows, the cue narrows with it.
    val elevated = config?.canManageUsers == true

    // A *delayed* loading flag so a fast load never flashes the cue (issue #469).
    val brandLoadingShown = useDelayedFlag(brandState is ShellBrand.Loading)

    // Open the group that holds the page on screen when you first arrive at it (issue #554); after that the
    // toggle owns the state, so the group you are in can still be collapsed. Keyed on that group, so navigating
    // within it does not reopen a group you closed, while arriving at a different group opens it.
    val currentGroup = currentGroupId(config?.menu.orEmpty(), props.currentPage)
    useEffect(currentGroup) {
        if (currentGroup != null && currentGroup !in expandedGroups) {
            expandedGroups = expandedGroups + currentGroup
        }
    }

    // The menu scrolls within itself past the window's edge (issue #1303), so what you are looking for is brought
    // into view rather than left below it. On opening, that is the page on screen: its item, or -- when you have
    // collapsed the group holding it -- that group's marked header.
    useEffect(open) {
        if (!open) return@useEffect
        val current = document.querySelector("#$navMenuId .app-menu-item.is-current")
            ?: document.querySelector("#$navMenuId .app-menu-parent.is-current")
        if (current != null) revealInMenu(current, current)
    }
    // On expanding a group, its header and children -- as many as fit, the header first. Keyed on the group just
    // expanded (cleared by a collapse), so the effect runs once per expansion, after the children are drawn.
    var justExpanded by useState<String?>(null)
    useEffect(justExpanded) {
        val header = justExpanded?.let { document.getElementById(menuParentId(it)) } ?: return@useEffect
        revealInMenu(header, header.nextElementSibling ?: header)
    }

    header {
        className = ClassName(if (elevated) "app-bar admin" else "app-bar")
        a {
            className = ClassName("app-bar-brand")
            href = routeHref(emptyList())
            img {
                className = ClassName("app-bar-logo")
                // The client's own mark where it has set one (issue #1130), else the deployment's.
                src = markUrlFor(config)
                // Decorative: the wordmark beside it already carries the name, so alt text here would only
                // make a screen reader announce the brand twice.
                alt = ""
            }
            // Copy, not a literal (issue #456): the same `home.brand` the hero renders, so a client that
            // overlays it is renamed in both places at once and neither knows the client exists. The mark
            // (above) always carries the bar; what follows it is the wordmark's three-state (issue #469).
            //
            // A literal fallback ("KDR") is deliberately avoided -- it would be *wrong* for a moment for anybody
            // whose brand differs, and flicker on every load, the same reasoning as `identityLabel`.
            when (val b = brandState) {
                // Loaded: the wordmark, or nothing when the deployment names no brand (the mark stands alone).
                is ShellBrand.Ready -> b.label?.let { +it }
                // Failed: mark alone. The chrome is the wrong place to explain an outage -- the page body does,
                // through its error boundary; a blank bar here reads as "loading", which this exists to stop.
                ShellBrand.Failed -> {}
                // Loading: a quiet cue beside the mark, but only after a short hold so a fast load never flashes it.
                ShellBrand.Loading -> if (brandLoadingShown) {
                    span {
                        className = ClassName("app-bar-brand-loading")
                        +"…"
                    }
                }
            }
        }
        div {
            className = ClassName("app-bar-right")

            // Session status sits together on the right, beside the identity it qualifies, rather than
            // floating between the brand and the menu: the bar is `space-between`, so a loose badge in the
            // middle drifts with the window width. Read left to right, the cluster is the session's
            // attributes (env, admin) and then who it belongs to.

            // The env-auth badge, which is also the control that turns it off (issue #360). Shown whenever env
            // auth is AVAILABLE, never on the effective state -- suppress it and this is the one affordance
            // that must survive, or there is no way back.
            //
            // A button rather than a span, unlike the badges around it: this one does something, and something
            // a keyboard user must be able to reach. It names the *state*, not the action, so the bar reads as
            // a description of where you are rather than a row of commands.
            if (props.envAuthSuppressible) {
                // A plain on/off toggle (issues #360, #517): acting -> off ("browse as an ordinary user"),
                // off -> on. Debug is **not** the badge's concern -- it is owned by the Debug menu, which shows
                // the state and offers "Turn off debug" with a label. So the badge no longer carries a third
                // state: it would only duplicate the menu, and a badge that could reach debug is the accidental-
                // activation path slice 1's review flagged. Suppressing here still clears debug on the backend
                // (debug requires effective env auth), so on -> off from either surface leaves the same state.
                val acting = props.envAuthActing
                button {
                    className = ClassName("bar-badge env-badge" + if (acting) "" else " off")
                    title = if (acting) {
                        "You reached this deployment through an authenticated environment. Click to browse as an ordinary user."
                    } else {
                        "Environment access is suppressed for this session. Click to restore it."
                    }
                    onClick = {
                        appBarScope.launch {
                            setEnvAuthOp(if (acting) EnvAuthOp.suppress else EnvAuthOp.restore)
                            bump()
                        }
                    }
                    +if (acting) "Env" else "Env off"
                }
            }
            // The persistent `_debug` box (issue #517, slice 3), beside the badge and shown only in debug. Its
            // value rides the `X-Kdr-Debug` header on every request (see `Http`), so an operator turns on a
            // diagnostic tag -- `explainAccess`, `explainScope` -- without editing URLs. A change persists to
            // this browser's storage immediately (so a reload keeps it) and bumps the refresh generation, so a
            // mounted view re-fetches with the new tag rather than waiting for the next navigation.
            if (props.envAuthDebug) {
                // Valid when blank (blank clears it) or every comma-separated entry is a variable name within
                // the length cap -- the *same* rules the backend applies, run here through the kernel's own
                // `splitComma`/`isVariableName` (compiled to JS) so a typo shows as invalid rather than being
                // silently dropped server-side with no sign anything is wrong.
                val valid = debugTags.length <= EP.debugMaxLength &&
                    (debugTags.isBlank() || debugTags.splitComma().all { it.isVariableName() })
                input {
                    className = ClassName("bar-badge env-debug-input" + if (valid) "" else " invalid")
                    value = debugTags
                    // A hint at what to type, not the wire key: "_debug" as a placeholder read as a mystery
                    // label. The exact key still appears in the tooltip and the accessible name below.
                    placeholder = "debug tag"
                    asDynamic()["aria-label"] = "Debug request tags, sent as ${EP.debug} on every request"
                    asDynamic()["aria-invalid"] = !valid
                    title = if (valid) {
                        "Comma-separated ${EP.debug} tags (e.g. explainAccess) sent on every request. " +
                            "Kept for this browser session; hidden but not cleared when you leave debug."
                    } else {
                        "Not applied: each tag must be a variable name (letters, digits, underscore), " +
                            "${EP.debugMaxLength} characters at most."
                    }
                    onChange = { e -> debugTags = e.target.value }
                    // Persist and apply on commit rather than per keystroke: a half-typed tag should not ride a
                    // request, and re-fetching on every character would be noise. Only when the value actually
                    // changed and is valid -- an unchanged blur must not bump the refresh generation, and an
                    // invalid value is left showing (so it can be fixed) but neither stored nor sent.
                    onBlur = {
                        if (valid && debugTags.trim() != debugRequestTags()) {
                            setDebugRequestTags(debugTags)
                            bump()
                        }
                    }
                }
                // The words the node declares (issue #1334), each with what it does, appended to the box on a pick.
                // A Select that never holds a value: it shows its placeholder, and a pick applies at once, as
                // committing the box does. A word the box already has is shown but cannot be picked again.
                if (props.debugTags.isNotEmpty()) {
                    val present = debugTagWords(debugTags)
                    Select {
                        this.size = "small"
                        this.value = null
                        this.placeholder = "+ tag"
                        this.popupMatchSelectWidth = 420
                        this.style = js("({ width: 72 })")
                        asDynamic()["aria-label"] = "Add a debug tag to the box"
                        this.options = props.debugTags.map { tag ->
                            val option: dynamic = js("({})")
                            option.value = tag.name
                            option.disabled = tag.name in present
                            option.label = DebugTagOption.create { this.tag = tag }
                            option
                        }.toTypedArray()
                        this.onChange = { picked ->
                            val next = withDebugTag(debugTags, picked as String)
                            debugTags = next
                            if (next != debugRequestTags()) {
                                setDebugRequestTags(next)
                                bump()
                            }
                        }
                    }
                }
            }
            // Spelled out, not just colored: a hue on its own tells a colourblind user nothing, and this is
            // the cue that says "the actions available to you right now are privileged". Shares the .bar-badge
            // chip look with the env badge so the two read as siblings rather than two unrelated stickers.
            // Design View's switch (issue #972), beside the admin cue it belongs to. Like the env badge it names the
            // state and is the control: on, the workflow pages mark each part of a form with the definition behind
            // it. Offered to anyone with administrative rights; the backend decides whether to answer, and a page it
            // does not answer simply draws as usual. Flipping it bumps the refresh generation so a mounted page
            // re-reads its view with (or without) the explanation.
            if (elevated) {
                val designOn = designViewRequested()
                button {
                    className = ClassName("bar-badge design-badge" + if (designOn) " on" else "")
                    asDynamic()["aria-pressed"] = designOn
                    title = if (designOn) {
                        "Design view is on: forms show what defines each part. Click to turn it off."
                    } else {
                        "Turn on Design view to see what defines each part of a form."
                    }
                    onClick = {
                        setDesignViewRequested(!designOn)
                        bump()
                    }
                    +"Design"
                }
            }
            if (elevated) {
                span {
                    className = ClassName("bar-badge admin-badge")
                    +"Admin"
                }
            }
            // The sandbox's place in the bar (issue #931): outside a sandbox, "Open sandbox" for an administrator of a
            // client that has one; inside one, "Acme Sandbox" -- for anyone there, and always on screen as the bar is,
            // so nobody mistakes the sandbox for the real client. The chip opens a popover saying what the sandbox is,
            // with the way back: a switch (issue #749) to the person's own user in that client.
            // A shared demo space (issue #1248): a standing note on every page, since the bar is on every page, that
            // what the caller does here other visitors see. Clicked, it says what that means.
            if (config?.sharedDemo == true) {
                val clientName = config?.users.orEmpty().firstOrNull { it.isCurrent }?.clientName ?: config?.user?.client.orEmpty()
                button {
                    className = ClassName("bar-badge shared-demo-chip")
                    asDynamic()["aria-haspopup"] = "dialog"
                    asDynamic()["aria-expanded"] = sharedDemoInfoOpen
                    title = "A shared demo space: other visitors see what you do here. Click for more."
                    onClick = { sharedDemoInfoOpen = !sharedDemoInfoOpen }
                    +"Shared demo"
                }
                if (sharedDemoInfoOpen) {
                    div {
                        className = ClassName("app-menu-overlay")
                        onClick = { sharedDemoInfoOpen = false }
                    }
                    div {
                        className = ClassName("app-menu sandbox-info")
                        asDynamic()["role"] = "dialog"
                        p { +sharedDemoNoteText(clientName) }
                    }
                }
            }
            val inSandbox = config?.sandboxOf
            if (inSandbox != null) {
                button {
                    className = ClassName("bar-badge sandbox-chip")
                    asDynamic()["aria-haspopup"] = "dialog"
                    asDynamic()["aria-expanded"] = sandboxInfoOpen
                    title = "You are in a sandbox. Click for what that means, and the way back."
                    onClick = { sandboxInfoOpen = !sandboxInfoOpen }
                    +sandboxChipLabel(inSandbox)
                }
                if (sandboxInfoOpen) {
                    div {
                        className = ClassName("app-menu-overlay")
                        onClick = { sandboxInfoOpen = false }
                    }
                    div {
                        className = ClassName("app-menu sandbox-info")
                        asDynamic()["role"] = "dialog"
                        val users = config?.users.orEmpty()
                        val back = sandboxWayBack(inSandbox, users)
                        // A stranded session with nowhere listed to go (issue #1197) is offered the exit that remains.
                        val hasWayOut = back != null || switchableUsers(users).isNotEmpty()
                        p { +sandboxInfoText(inSandbox, hasWayOut) }
                        // What a sandbox is, for whoever wants more than the line above (issue #1337) -- not to a
                        // session whose access has ended, which needs the way out rather than the concept.
                        if (!inSandbox.stranded) {
                            WhatIsThis { text = shellCopy.explanation(HFRAG.sandboxNs) }
                        }
                        back?.let { way ->
                            button {
                                className = ClassName("sandbox-back")
                                onClick = {
                                    sandboxInfoOpen = false
                                    switchUserAction(way.userId)
                                }
                                +"Back to ${inSandbox.name}"
                            }
                        }
                        if (inSandbox.stranded && !hasWayOut) {
                            button {
                                className = ClassName("sandbox-back")
                                onClick = {
                                    sandboxInfoOpen = false
                                    logoutAction()
                                }
                                +"Sign out"
                            }
                        }
                    }
                }
            } else if (showOpenSandbox(config)) {
                button {
                    className = ClassName("bar-badge sandbox-open")
                    title = "Open this client's sandbox, which runs its unpublished configuration."
                    onClick = { openSandboxAction() }
                    +"Open sandbox"
                }
            }
            // A quiet marker that this is the readable build (issue #230), deliberately NOT a .bar-badge chip:
            // it reports which bundle is loaded, not a fact about the session. Can never appear on a real
            // deployment, which ships the minified bundle where the check below is false by construction.
            if (isReadableBuild) {
                span {
                    className = ClassName("build-badge")
                    title = "Readable (development) web-app bundle — larger, but crashes name the Kotlin that failed."
                    +"readable build"
                }
            }
            // Identity in the bar itself, not only inside the menu (issue #276). Being signed out is a fact
            // worth stating: rendering nothing for it reads exactly like a config that has not arrived, so a
            // user cannot tell "I am signed out" from "this has not loaded". Null while genuinely unknown.
            //
            // For a person with several users (issue #749) the badge says which one this is, in brackets, and
            // is the control that switches: a button with a caret opening a menu of the others, the way a user
            // menu sits top-right in a conventional shell. With one user it stays the plain label it was.
            identityLabel(config != null, config?.user?.isLoggedIn == true, config?.user?.displayName)?.let { label ->
                val users = switchableUsers(config?.users.orEmpty())
                if (users.isEmpty()) {
                    span {
                        className = ClassName(if (label == signedOutLabel) "identity-badge signed-out" else "identity-badge")
                        +label
                    }
                } else {
                    button {
                        className = ClassName("identity-badge has-users")
                        asDynamic()["aria-haspopup"] = "menu"
                        asDynamic()["aria-expanded"] = userMenuOpen
                        // The list holds registered, enabled users only, so a session acting as one that is
                        // neither has no current entry here -- the badge then just offers the others.
                        title = users.firstOrNull { it.isCurrent }
                            ?.let { "${identityBadgeTitle(it, config?.user?.roles)} Click to switch users." }
                            ?: "Click to switch users."
                        onClick = { userMenuOpen = !userMenuOpen }
                        +identityBadgeText(label, users, config?.user?.roles)
                        span { className = ClassName("app-menu-caret"); +(if (userMenuOpen) "▲" else "▼") }
                    }
                    if (userMenuOpen) {
                        div {
                            className = ClassName("app-menu-overlay")
                            onClick = { userMenuOpen = false }
                        }
                        userMenu(users, config?.user?.roles) { switchUserAction(it) }
                    }
                }
            }
            button {
                className = ClassName("hamburger")
                onClick = { open = !open }
                +"☰"
            }
            if (open) {
                div {
                    className = ClassName("app-menu-overlay")
                    onClick = { open = false }
                }
                div {
                    className = ClassName("app-menu")
                    // Its own id, so the reveal effects above look in this menu and not the user menu, which
                    // shares the `.app-menu` and `.is-current` classes.
                    asDynamic()["id"] = navMenuId

                    // No "signed in as" label here any more (issue #276). Identity moved to the bar, which is
                    // visible whether or not the menu is open -- and the bar is still on screen while it is,
                    // so a label here only repeated the same string a few pixels away. The menu is for
                    // actions; saying who you are is the bar's job.

                    // The items themselves, exactly as the backend composed them for this caller, nested into a
                    // drill-down where an item names a parent (issue #517). Whether an item is *current* is
                    // decided by its own route against the page on screen, not by matching one winning item:
                    // two items routing to the same page (a client shortcut beside the built-in) are then both
                    // current, and re-choosing either restarts the page rather than only the first (issue #565).
                    for (node in menuTree(config?.menu.orEmpty())) {
                        if (node.children.isEmpty()) {
                            menuItemView(node.item, frontendActions, props.currentPage, props.onRevisit) { open = false }
                        } else {
                            // A parent: its label is a header, and its children drill down indented under it.
                            // The parent carries no action of its own -- the backend `collectParentIssues` boot
                            // check refuses one, precisely because this render would discard it (issue #517) --
                            // so drawing it as a plain header loses nothing. The header labels the child group
                            // for assistive tech via `aria-labelledby`, matching the `role="group"` below.
                            val headerId = menuParentId(node.item.id)
                            // The header is marked when the group holds the page on screen; a collapsed group is
                            // opened on arrival by the effect above, not by forcing `expanded` here -- otherwise the
                            // toggle could never close the group you are in (a silent no-op, webapp/CLAUDE.md).
                            val holdsCurrent = node.children.any { (it.action as? UiRoute)?.page == props.currentPage }
                            val expanded = node.item.id in expandedGroups
                            button {
                                // id/aria via asDynamic to match this file's idiom (see menuItemView below).
                                asDynamic()["id"] = headerId
                                className = ClassName(if (holdsCurrent) "app-menu-parent is-current" else "app-menu-parent")
                                // A real toggle now: aria-expanded states it, and the children below render
                                // only when open, so the group collapses to a single line until asked for.
                                asDynamic()["aria-expanded"] = expanded
                                onClick = {
                                    val next = if (expanded) expandedGroups - node.item.id
                                        else expandedGroups + node.item.id
                                    expandedGroups = next
                                    writeExpandedGroups(next)
                                    justExpanded = if (expanded) null else node.item.id
                                }
                                // Label on the left, a dropdown-style chevron on the right: down when it can be
                                // opened, up when it can be closed.
                                span { +node.item.label }
                                span { className = ClassName("app-menu-caret"); +(if (expanded) "\u25b2" else "\u25bc") }
                            }
                            if (expanded) {
                                div {
                                    className = ClassName("app-menu-children")
                                    asDynamic()["role"] = "group"
                                    asDynamic()["aria-labelledby"] = headerId
                                    for (child in node.children) {
                                        menuItemView(child, frontendActions, props.currentPage, props.onRevisit) { open = false }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** The menu groups the caller has expanded (issue #540), remembered per browser under [expandedGroupsStorageKey].
 *  Absent/blank -> every group collapsed, which is the default (children on demand). */
private const val expandedGroupsStorageKey = "kdrMenuExpandedGroups"

// `localStorage` (not sessionStorage): the choice should outlive the tab, like a remembered filter. A store that
// cannot be read leaves the menu all-collapsed rather than breaking (see BrowserStorage.kt).
private fun readExpandedGroups(): Set<String> =
    (localStorageGet(expandedGroupsStorageKey, "menu state")?.splitComma() ?: emptyList()).toSet()

private fun writeExpandedGroups(ids: Set<String>) =
    localStorageSet(expandedGroupsStorageKey, ids.joinToString(","), "menu state")

/** A short delay before a loading cue appears, so a fast load never flashes it then removes it (issue #469). */
private const val brandFlashDelayMs = 200

/**
 * True only once [active] has held for [delayMs] -- and immediately false again when it clears (issue #469). The
 * conventional cure for a flashing indicator: a fast resolve never crosses the threshold, so nothing appears.
 */
fun useDelayedFlag(active: Boolean, delayMs: Int = brandFlashDelayMs): Boolean {
    var shown by useState(false)
    val timer = useRef<Int>(null)
    useEffect(active) {
        // Clear any pending timer from the previous run first, so a quick active→inactive→active never leaves
        // two racing. (This codebase does its timer cleanup on the next run rather than via an effect-cleanup
        // callback -- see the debounce in Users.kt.)
        timer.current?.let { clearTimer(it) }
        timer.current = null
        if (!active) {
            shown = false
        } else if (!shown) {
            timer.current = setTimer({ shown = true }, delayMs)
        }
    }
    return shown
}

/** The browser's `setTimeout`/`clearTimeout`, declared locally rather than reaching for a DOM wrapper. */
private fun setTimer(block: () -> Unit, delayMs: Int): Int = js("setTimeout(block, delayMs)") as Int
private fun clearTimer(id: Int) {
    js("clearTimeout(id)")
}

/**
 * The identity badge's menu (issue #749): one row per user, each showing what tells it from the others in the
 * same bracket style as the badge (`UserChoice.qualifierWithin`), the full label as its tooltip; the current
 * user marked like the page on screen and inert -- you are already them. Not items from the menu block: they
 * are per caller and change as users are made, so they ride the shell config's `users`.
 */
private fun ChildrenBuilder.userMenu(users: List<UserChoice>, roles: Collection<String>?, onSwitch: (Long) -> Unit) {
    div {
        className = ClassName("app-menu user-menu")
        asDynamic()["role"] = "menu"
        for (user in users) {
            button {
                className = ClassName(if (user.isCurrent) "app-menu-item is-current" else "app-menu-item")
                asDynamic()["role"] = "menuitem"
                if (user.isCurrent) asDynamic()["aria-current"] = "true"
                disabled = user.isCurrent
                title = user.label()
                onClick = { onSwitch(user.userId) }
                +userMenuEntryText(user, users, roles)
            }
        }
    }
}

/** One anchor menu item; a plain click navigates (see `followAppLink`), which the router and this bar react to. */
private fun ChildrenBuilder.menuLink(href: String, label: String, current: Boolean, onClick: () -> Unit) {
    a {
        className = ClassName(if (current) "app-menu-item is-current" else "app-menu-item")
        // The page on screen (issue #554): stated for assistive tech, not only by colour.
        if (current) asDynamic()["aria-current"] = "page"
        this.href = href
        this.onClick = { onClick() }
        +label
    }
}

/**
 * The menu item whose route is [page] -- the page on screen -- or null when the menu offers no route to it
 * (Home, a document, a page reached by URL alone). Only a route can be current: a group header or a call has
 * no page. Pure, covered under `jsNodeTest` (issue #554).
 */
fun currentMenuItem(items: List<MenuItem>, page: String): MenuItem? =
    items.firstOrNull { (it.action as? UiRoute)?.page == page }

/** The id of the drill-down group whose child is the page on screen, or null when the current item is
 *  top-level or the menu offers no route to [page]. What the bar opens on arrival (issue #554). */
fun currentGroupId(items: List<MenuItem>, page: String): String? = currentMenuItem(items, page)?.parentId

/** The element id of the navigation menu, and of a group's header within it. */
private const val navMenuId = "app-nav-menu"
private fun menuParentId(itemId: String) = "menu-parent-$itemId"

/**
 * Scrolls the navigation menu -- and only it, never the page -- so that the run from [first] to [last] is in view
 * (issue #1303). `scrollIntoView` was the obvious call, but it scrolls every ancestor that can, the window
 * included, and cannot say "this run, its top first".
 */
private fun revealInMenu(first: Element, last: Element) {
    val menu = document.getElementById(navMenuId) ?: return
    val box = menu.getBoundingClientRect()
    val delta = menuScrollDelta(
        box.top + menu.clientTop, box.top + menu.clientTop + menu.clientHeight,
        first.getBoundingClientRect().top, last.getBoundingClientRect().bottom,
    )
    if (delta != 0.0) menu.scrollTop += delta
}

/**
 * How far to scroll a scrolling box whose visible part runs from [viewTop] to [viewBottom] so that a run from
 * [runTop] to [runBottom] (all in the same coordinates) is in view: not at all when it already is; otherwise the
 * least that shows its bottom, but never so far that its top leaves -- a run taller than the box shows its top,
 * which for a group is its header. Positive scrolls down. Pure, covered under `jsNodeTest` (issue #1303).
 */
fun menuScrollDelta(viewTop: Double, viewBottom: Double, runTop: Double, runBottom: Double): Double = when {
    runTop < viewTop -> runTop - viewTop
    runBottom > viewBottom -> minOf(runBottom - viewBottom, runTop - viewTop)
    else -> 0.0
}

/** A top-level menu item together with the items that name it as their [MenuItem.parentId] (issue #517). */
class MenuNode(val item: MenuItem, val children: List<MenuItem>)

/**
 * Groups a flat menu into a one-level drill-down (issue #517): each top-level item (no [MenuItem.parentId])
 * paired with the items that name it as their parent, in the order the backend sent them. A child whose named
 * parent is absent (its cfact dropped it) is dropped too, so an orphan never renders parentless. And a group
 * header -- an item with no action -- whose children were all dropped is dropped as well, so a header never renders
 * bare: a group may mix audiences, its header showing wherever any of its children does. Pure, covered by
 * `jsNodeTest`.
 */
fun menuTree(items: List<MenuItem>): List<MenuNode> {
    val childrenByParent = items.filter { it.parentId != null }.groupBy { it.parentId }
    return items.filter { it.parentId == null }
        .map { MenuNode(it, childrenByParent[it.id].orEmpty()) }
        .filter { it.item.action != null || it.children.isNotEmpty() }
}

/** Renders one leaf menu item -- a route as a link, a call as a button (issue #483); a null action renders
 *  nothing (a parent's label is drawn by the drill-down, not here). A route is *current* when it names
 *  [currentPage]. [onNavigate] closes the menu; [onRevisit] fires when the route chosen is the one already on
 *  screen, since the address cannot announce that (issue #565). */
private fun ChildrenBuilder.menuItemView(
    item: MenuItem, actions: FrontendActions, currentPage: String, onRevisit: () -> Unit, onNavigate: () -> Unit,
) {
    when (val action = item.action) {
        is UiRoute -> {
            val current = action.page == currentPage
            menuLink(routeHref(listOf(HP.page to action.page)), item.label, current) {
                onNavigate()
                // The same address moves nothing: this is the only signal the router gets that the page was re-chosen.
                if (current) onRevisit()
            }
        }
        is UiCall -> button {
            className = ClassName("app-menu-item")
            // An `openPath` call leaves the SPA for a server path (issue #493); its accessible name says so.
            val leavesApp = action.def.name == HACT.openPath.name
            if (leavesApp) {
                asDynamic()["aria-label"] = "${item.label} (leaves this app)"
            }
            onClick = {
                onNavigate()
                // An unimplemented name closes the menu and does nothing else, which is what the startup check
                // exists to stop ever reaching a person.
                actions.run(action.def.name, action.args)
            }
            +item.label
            if (leavesApp) {
                span {
                    className = ClassName("app-menu-external")
                    asDynamic()["aria-hidden"] = true
                    +" ↗"
                }
            }
        }
        null -> Unit
    }
}

private external interface DebugTagOptionProps : Props {
    var tag: DebugTagChoice
}

/** One word in the debug-tag picker (issue #1334): the word and what kind of word it is, then what it does. */
private val DebugTagOption = FC<DebugTagOptionProps> { props ->
    div {
        className = ClassName("debug-tag-option")
        div {
            span {
                className = ClassName("debug-tag-name")
                +props.tag.name
            }
            span {
                className = ClassName("debug-tag-kind")
                +debugTagKindLabel(props.tag.kind)
            }
        }
        div {
            className = ClassName("debug-tag-description")
            +props.tag.description
        }
    }
}
